import { useState } from "react";
import { Form, useNotify, useTranslate } from "ra-core";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { nativeSelectClass } from "@/tenax/tasks/taskData";
import { HolderFields, NoReferralCodeWarning } from "./HolderFields";
import { useHolderValues } from "./racketHooks";
import {
  fetchRacketByCode,
  findByCode,
  modelOf,
  useLoanPurposes,
  useRacketCommand,
  useRacketOverview,
  useVariants,
  type RacketOverview,
} from "./racketData";
import { ScanField } from "./ScanField";

export interface LendTarget {
  contactId?: number | null;
  companyId?: number | null;
  dealId?: number | null;
}

// Loan of one or more test rackets in the house (task 1.10b, D-040): from a
// deal, a person or an organization the holder is given; from the rackets
// page it is chosen. Rackets by barcode, or by hand from the list. From a
// deal the box "move to Prova inviata" starts checked.
export const LendDialog = ({
  target,
  onClose,
}: {
  target: LendTarget;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const hasHolder = target.contactId != null || target.companyId != null;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto"
        // the barcode field takes the focus, so the first shot lands there
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          document.getElementById("lend-scan")?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{translate("tenax.rackets.lend.title")}</DialogTitle>
          <DialogDescription>
            {translate("tenax.rackets.lend.hint")}
          </DialogDescription>
        </DialogHeader>
        <Form
          defaultValues={{
            contact_id: target.contactId ?? null,
            company_id: target.companyId ?? null,
          }}
        >
          <LendBody target={target} showHolder={!hasHolder} onClose={onClose} />
        </Form>
      </DialogContent>
    </Dialog>
  );
};

const LendBody = ({
  target,
  showHolder,
  onClose,
}: {
  target: LendTarget;
  showHolder: boolean;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const run = useRacketCommand();
  const { contactId, companyId } = useHolderValues();
  const { data: purposes = [] } = useLoanPurposes();
  const { data: variants = [] } = useVariants();
  const { data: inHouse = [] } = useRacketOverview({ status: "in_house" });
  const activePurposes = purposes.filter((purpose) => purpose.active);
  const [purposeId, setPurposeId] = useState<number | null>(null);
  const [expected, setExpected] = useState("");
  const [moveDeal, setMoveDeal] = useState(true);
  const [picked, setPicked] = useState<RacketOverview[]>([]);
  const [saving, setSaving] = useState(false);
  const purpose = purposeId ?? activePurposes[0]?.id ?? null;
  const available = inHouse.filter(
    (racket) => !picked.some((item) => item.id === racket.id),
  );

  const add = (racket?: RacketOverview) => {
    if (!racket) return;
    setPicked((current) =>
      current.some((item) => item.id === racket.id)
        ? current
        : [...current, racket],
    );
  };
  const onCode = async (code: string) => {
    const found = findByCode(inHouse, code) ?? (await fetchRacketByCode(code));
    if (found?.status !== "in_house") {
      notify("tenax.rackets.lend.not_in_house", {
        type: "warning",
        messageArgs: { code },
      });
      return;
    }
    add(found);
  };

  const submit = async () => {
    if (contactId == null && companyId == null) {
      notify("tenax.rackets.need_holder", { type: "error" });
      return;
    }
    setSaving(true);
    const ok = await run(
      "tenax_lend_test_rackets",
      {
        p_racket_ids: picked.map((racket) => racket.id),
        p_contact_id: contactId,
        p_company_id: companyId,
        p_purpose_id: purpose,
        p_expected_return_at: expected || null,
        p_deal_id: target.dealId ?? null,
        p_move_deal_to_trial: target.dealId != null && moveDeal,
      },
      "tenax.rackets.lend.done",
    );
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <div className="flex flex-col gap-4">
      <ScanField
        id="lend-scan"
        label={translate("tenax.rackets.scan")}
        onCode={onCode}
        autoFocus
      />
      {showHolder ? <HolderFields /> : null}
      <NoReferralCodeWarning contactId={contactId} companyId={companyId} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lend-purpose">
          {translate("tenax.rackets.fields.purpose_id")}
        </Label>
        <select
          id="lend-purpose"
          className={nativeSelectClass}
          value={purpose ?? ""}
          onChange={(event) => setPurposeId(Number(event.target.value))}
        >
          {activePurposes.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lend-pick">
          {translate("tenax.rackets.lend.pick")}
        </Label>
        <select
          id="lend-pick"
          className={nativeSelectClass}
          value=""
          onChange={(event) =>
            add(available.find((r) => r.id === Number(event.target.value)))
          }
        >
          <option value="">—</option>
          {available.map((racket) => (
            <option key={racket.id} value={racket.id}>
              {racket.code} · {modelOf(racket, variants)}
            </option>
          ))}
        </select>
      </div>
      <ul
        aria-label={translate("tenax.rackets.lend.picked")}
        className="flex flex-col gap-1"
      >
        {picked.map((racket) => (
          <li
            key={racket.id}
            className="flex items-center justify-between rounded-md border px-2 py-1 text-sm"
          >
            <span>
              <span className="font-mono">{racket.code}</span> ·{" "}
              {modelOf(racket, variants)}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={translate("ra.action.remove")}
              onClick={() =>
                setPicked(picked.filter((item) => item.id !== racket.id))
              }
            >
              <X className="size-4" />
            </Button>
          </li>
        ))}
      </ul>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lend-expected">
          {translate("tenax.rackets.fields.expected_return_at")}
        </Label>
        <Input
          id="lend-expected"
          type="date"
          value={expected}
          onChange={(event) => setExpected(event.target.value)}
        />
      </div>
      {target.dealId != null ? (
        <div className="flex items-center gap-2">
          <Checkbox
            id="lend-move-deal"
            checked={moveDeal}
            onCheckedChange={(value) => setMoveDeal(value === true)}
          />
          <Label htmlFor="lend-move-deal">
            {translate("tenax.rackets.lend.move_deal")}
          </Label>
        </div>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {translate("ra.action.cancel")}
        </Button>
        <Button
          type="button"
          disabled={!picked.length || purpose == null || saving}
          onClick={submit}
        >
          {translate("tenax.rackets.lend.confirm", {
            smart_count: picked.length,
          })}
        </Button>
      </DialogFooter>
    </div>
  );
};
